import { Button } from 'antd';
import { CheckCircleOutlined, WhatsAppOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { SEO } from '../../components/common/SEO';
import { MarketingLayout } from './MarketingLayout';

/**
 * /features/ecocash-payments - prerendered feature page targeting searches like
 * "pay school fees with EcoCash" and "EcoCash school fees Zimbabwe".
 */
export const EcocashFeaturePage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <MarketingLayout current="/features/ecocash-payments">
      <SEO
        title="Pay School Fees with EcoCash | Rongeka Africa"
        description="Parents pay school fees with EcoCash straight from their phone, and the payment is receipted against the student's account automatically. No bank queues, no manual capture for the bursar."
        url="https://rongeka.africa/features/ecocash-payments"
        keywords="pay school fees with EcoCash, EcoCash school fees, online school fees payment Zimbabwe, mobile money school payments, Pesepay school fees"
      />

      <header className="mkt-hero">
        <div className="landing-section-label">ONLINE PAYMENTS</div>
        <h1 className="mkt-hero-title">School Fees Paid with EcoCash, Receipted Automatically</h1>
        <p className="mkt-hero-lead">
          Parents pay from the phone in their pocket. The payment lands against the right
          student's account with a receipt issued automatically - no bank queue for the
          parent, no manual capture for the bursar.
        </p>
        <div className="mkt-hero-actions">
          <Button type="primary" size="large" onClick={() => navigate('/get-started')}>
            Get Started
          </Button>
          <Button
            size="large"
            icon={<WhatsAppOutlined />}
            onClick={() => window.open('https://wa.me/263789442202?text=hi', '_blank')}
          >
            Ask Us About Online Payments
          </Button>
        </div>
      </header>

      <section className="mkt-section">
        <div className="mkt-content">
          <h2 className="mkt-section-heading">How an EcoCash fee payment works</h2>
          <ol className="mkt-steps">
            <li>
              <strong>Parent gets the invoice</strong>
              The fee invoice or balance arrives on WhatsApp, with a secure payment link.
            </li>
            <li>
              <strong>They pay with EcoCash</strong>
              The parent confirms the payment on their phone with their EcoCash PIN. Card
              payments are supported too.
            </li>
            <li>
              <strong>Rongeka receipts it instantly</strong>
              The payment is matched to the student's fee account, a receipt is issued, and
              the balance updates - with no one at the school touching a thing.
            </li>
          </ol>
        </div>
      </section>

      <section className="mkt-section mkt-section-alt">
        <div className="mkt-content">
          <h2 className="mkt-section-heading">Why schools switch fees to mobile money</h2>
          <div className="mkt-grid">
            <div className="mkt-card">
              <h3>For parents</h3>
              <ul className="mkt-check-list">
                <li><CheckCircleOutlined />Pay any time, from anywhere - no bank queue, no trip to school</li>
                <li><CheckCircleOutlined />Instant confirmation and receipt on their phone</li>
                <li><CheckCircleOutlined />Balance visible on WhatsApp before and after paying</li>
              </ul>
            </div>
            <div className="mkt-card">
              <h3>For the bursar</h3>
              <ul className="mkt-check-list">
                <li><CheckCircleOutlined />Payments arrive already matched to the right student</li>
                <li><CheckCircleOutlined />No manual capture, no misallocated deposits</li>
                <li><CheckCircleOutlined />Less cash on the premises to count, store, and secure</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="mkt-section">
        <div className="mkt-content">
          <h2 className="mkt-section-heading">Secure payments through Pesepay</h2>
          <p className="mkt-section-body">
            Online payments in Rongeka are processed by Pesepay, a licensed Zimbabwean payment
            gateway supporting EcoCash and bank cards. Rongeka never stores card numbers or
            wallet PINs - parents authorise every payment on their own device, and the school
            receives settlement through its normal Pesepay account. Online payments are
            switched on for your school on request, at no extra platform charge from Rongeka.
          </p>
        </div>
      </section>
    </MarketingLayout>
  );
};
